import React, { useState, useEffect } from 'react';
import './RPAWizardModal.css';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';

// ─────────────────────────────────────────────────────────────────────────────
// TDS Wizard — field keys are the EXACT California TDS fillable-PDF AcroForm
// field names (see TDS_Form_Fields_Schema.json). The wizard collects values
// keyed by those names, so the save/submit payload maps 1:1 onto the PDF with
// no backend translation. Checkbox -> boolean, radio -> "Yes"/"No"/null (or the
// field's enum), text -> string. Multi-line PDF explanation fields
// (…_line_1/2/3…) are entered as a single textarea and split by length at
// payload time.
// ─────────────────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, title: 'Property & Setup' },
  { id: 2, title: 'Appliances & Kitchen' },
  { id: 3, title: 'Systems & Climate' },
  { id: 4, title: 'Utilities, Safety & Garage' },
  { id: 5, title: 'Significant Defects' },
  { id: 6, title: 'Seller Awareness' },
  { id: 7, title: 'Agent Inspection' },
  { id: 8, title: 'Review & Confirm' },
];
const TOTAL_STEPS = STEPS.length;

const STEP_ICONS = {
  1: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>,
  2: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2h18v4H3zM7 6v14M17 6v14M3 10h18M3 14h18M3 18h18"/></svg>,
  3: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v3M12 20v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M1 12h3M20 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></svg>,
  4: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  5: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  6: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>,
  7: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  8: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
};

// ── Checkbox field groups (key = PDF field name) ────────────────────────────────
const CB_KITCHEN = [
  ['property_has_range', 'Range'],
  ['property_has_oven', 'Oven'],
  ['property_has_microwave', 'Microwave'],
  ['property_has_dishwasher', 'Dishwasher'],
  ['property_has_trash_compactor', 'Trash Compactor'],
  ['property_has_garbage_disposal', 'Garbage Disposal'],
  ['property_has_washer_dryer_hookups', 'Washer/Dryer Hookups'],
  ['property_has_water_softener', 'Water Softener'],
  ['property_has_water_conserving_plumbing_fixtures', 'Water-Conserving Plumbing Fixtures'],
  ['property_has_rain_gutters', 'Rain Gutters'],
];
const CB_HVAC = [
  ['property_has_central_heating', 'Central Heating'],
  ['property_has_central_air_conditioning', 'Central Air Conditioning'],
  ['property_has_evaporator_cooler_s', 'Evaporator Cooler(s)'],
  ['property_has_wall_window_air_conditioning', 'Wall/Window Air Conditioning'],
  ['property_has_sprinklers', 'Sprinklers'],
];
const CB_WATER_HEATER_FUEL = [
  ['water_heater_energy_gas', 'Gas'],
  ['water_heater_energy_solar', 'Solar'],
  ['water_heater_energy_electric', 'Electric'],
];
const CB_POOL_SPA = [
  ['property_has_pool', 'Pool'],
  ['property_has_pool_child_resistant_barrier', 'Child-Resistant Barrier'],
  ['property_has_hot_tub_spa', 'Hot Tub/Spa'],
  ['property_has_hot_tub_spa_locking_safety_cover', 'Locking Safety Cover'],
  ['property_has_sauna', 'Sauna'],
];
const CB_POOL_HEATER_FUEL = [
  ['pool_spa_heater_energy_gas', 'Gas'],
  ['pool_spa_heater_energy_solar', 'Solar'],
  ['pool_spa_heater_energy_electric', 'Electric'],
];
const CB_WATER_SOURCE = [
  ['water_supply_source_city', 'City'],
  ['water_supply_source_well', 'Well'],
  ['water_supply_source_private_utility_or', 'Private Utility / Other'],
];
const CB_GAS_SOURCE = [
  ['gas_supply_source_utility', 'Utility'],
  ['gas_supply_source_bottled_tank', 'Bottled (Tank)'],
];
const CB_SEWER = [
  ['property_has_public_sewer_system', 'Public Sewer System'],
  ['property_has_septic_tank', 'Septic Tank'],
  ['property_has_sump_pump', 'Sump Pump'],
];
const CB_SAFETY = [
  ['property_has_smoke_detector_s', 'Smoke Detector(s)'],
  ['property_has_carbon_monoxide_device_s', 'Carbon Monoxide Device(s)'],
  ['property_has_fire_alarm', 'Fire Alarm'],
  ['property_has_burglar_alarms', 'Burglar Alarms'],
  ['property_has_security_gate_s', 'Security Gate(s)'],
  ['property_has_window_screens', 'Window Screens'],
  ['property_has_window_security_bars', 'Window Security Bars'],
  ['property_has_bedroom_window_quick_release_mechanism', 'Quick-Release Mechanism on Bedroom Windows'],
];
const CB_MISC = [
  ['property_has_tv_antenna', 'TV Antenna'],
  ['property_has_satellite_dish', 'Satellite Dish'],
  ['property_has_intercom', 'Intercom'],
  ['property_has_patio_decking', 'Patio/Decking'],
  ['property_has_built_in_barbecue', 'Built-in Barbecue'],
  ['property_has_gazebo', 'Gazebo'],
];
const CB_GARAGE = [
  ['property_has_carport', 'Carport'],
  ['property_has_automatic_garage_door_opener_s', 'Automatic Garage Door Opener(s)'],
  ['property_has_garage_remote_controls', 'Garage Remote Controls'],
];

const DEFECTS = [
  ['defect_in_interior_walls', 'Interior Walls'],
  ['defect_in_ceilings', 'Ceilings'],
  ['defect_in_floors', 'Floors'],
  ['defect_in_exterior_walls', 'Exterior Walls'],
  ['defect_in_insulation', 'Insulation'],
  ['defect_in_roof_s', 'Roof(s)'],
  ['defect_in_windows', 'Windows'],
  ['defect_in_doors', 'Doors'],
  ['defect_in_foundation', 'Foundation'],
  ['defect_in_slab_s', 'Slab(s)'],
  ['defect_in_driveways', 'Driveways'],
  ['defect_in_sidewalks', 'Sidewalks'],
  ['defect_in_walls_fences', 'Walls/Fences'],
  ['defect_in_electrical_systems', 'Electrical Systems'],
  ['defect_in_plumbing_sewers_septics', 'Plumbing/Sewers/Septics'],
  ['defect_in_other_structural_components', 'Other Structural Components'],
];

// §II.C "Are you (Seller) aware of…" Yes/No radio questions, in form order.
const AWARENESS = [
  ['seller_aware_of_environmental_hazards', '1. Substances/materials/products that may be an environmental hazard (asbestos, formaldehyde, radon, lead-based paint, mold, fuel/chemical storage tanks, contaminated soil/water).'],
  ['seller_aware_of_shared_property_features', '2. Features shared in common with adjoining landowners (walls, fences, driveways) whose use/maintenance may affect the property.'],
  ['seller_aware_of_encroachments_or_easements', '3. Any encroachments, easements or similar matters that may affect your interest in the property.'],
  ['seller_aware_of_alterations_without_permits', '4. Room additions, structural modifications, or other alterations/repairs made without necessary permits.'],
  ['seller_aware_of_alterations_not_code_compliant', '5. Room additions, structural modifications, or other alterations/repairs not in compliance with building codes.'],
  ['seller_aware_of_fill_on_property', '6. Fill (compacted or otherwise) on the property or any portion thereof.'],
  ['seller_aware_of_settlement_or_soil_problems', '7. Any settling from any cause, or slippage, sliding, or other soil problems.'],
  ['seller_aware_of_flooding_drainage_or_grading_problems', '8. Flooding, drainage or grading problems.'],
  ['seller_aware_of_major_property_damage', '9. Major damage to the property/structures from fire, earthquake, floods, or landslides.'],
  ['seller_aware_of_zoning_or_setback_violations', '10. Any zoning violations, nonconforming uses, violations of "setback" requirements.'],
  ['seller_aware_of_neighborhood_noise_or_nuisances', '11. Neighborhood noise problems or other nuisances.'],
  ['seller_aware_of_ccrs_or_deed_restrictions', "12. CC&R's or other deed restrictions or obligations."],
  ['seller_aware_of_homeowners_association_authority', "13. Homeowners' Association which has any authority over the subject property."],
  ['seller_aware_of_coowned_common_areas', '14. Any "common area" (pools, tennis courts, walkways, or other areas co-owned in undivided interest with others).'],
  ['seller_aware_of_abatement_notices_or_citations', '15. Any notices of abatement or citations against the property.'],
  ['seller_aware_of_property_lawsuits_or_claims', '16. Any lawsuits by or against the Seller threatening or affecting this real property, including claims of a defect/deficiency.'],
];

// Single-textarea entry -> ordered PDF line fields it splits into.
const EXPLANATION_GROUPS = {
  additional_inspection_reports_description: ['additional_inspection_reports_description_line_1', 'additional_inspection_reports_description_line_2'],
  nonoperating_items_description: ['nonoperating_items_description_line_1', 'nonoperating_items_description_line_2'],
  other_structural_components_description: ['other_structural_components_description_line_1', 'other_structural_components_description_line_2'],
  significant_defects_explanation: ['significant_defects_explanation_line_1', 'significant_defects_explanation_line_2', 'significant_defects_explanation_line_3'],
  seller_awareness_explanation: ['seller_awareness_explanation_line_1', 'seller_awareness_explanation_line_2', 'seller_awareness_explanation_line_3', 'seller_awareness_explanation_line_4', 'seller_awareness_explanation_line_5'],
  seller_agent_inspection_items: ['seller_agent_inspection_items_line_1', 'seller_agent_inspection_items_line_2', 'seller_agent_inspection_items_line_3', 'seller_agent_inspection_items_line_4'],
  buyer_agent_inspection_items: ['buyer_agent_inspection_items_line_1', 'buyer_agent_inspection_items_line_2', 'buyer_agent_inspection_items_line_3', 'buyer_agent_inspection_items_line_4'],
};

// Approx. chars that fit on one PDF line field. Word-wrapped across the group's
// line fields; overflow beyond the last line is kept on the last line so no
// text is silently dropped.
const MAX_CHARS_PER_LINE = 95;

function splitIntoLines(text, lineKeys) {
  const out = {};
  lineKeys.forEach((k) => { out[k] = ''; });
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return out;
  const lines = [];
  let cur = '';
  for (const w of words) {
    const candidate = cur ? `${cur} ${w}` : w;
    if (candidate.length <= MAX_CHARS_PER_LINE || !cur) {
      cur = candidate;
    } else {
      lines.push(cur);
      cur = w;
    }
    if (lines.length === lineKeys.length - 1) {
      // last line — dump the remaining words here
      const idx = words.indexOf(w);
      const rest = words.slice(idx + 1);
      cur = rest.length ? `${cur} ${rest.join(' ')}` : cur;
      break;
    }
  }
  if (cur) lines.push(cur);
  lineKeys.forEach((k, i) => { out[k] = lines[i] || ''; });
  return out;
}

function joinLines(data, lineKeys) {
  return lineKeys.map((k) => data[k]).filter(Boolean).join(' ').trim();
}

// ── Small reusable sub-components ──────────────────────────────────────────────

function InfoBox({ type = 'info', children }) {
  return <div className={`rpa-info-box rpa-info-box--${type}`}>{children}</div>;
}
function SectionTitle({ children }) {
  return <div className="rpa-section-title">{children}</div>;
}
function Field({ label, hint, children, required }) {
  return (
    <div className="rpa-field">
      {label && (
        <label className="rpa-field-label">
          {label}{required && <span className="rpa-required"> *</span>}
        </label>
      )}
      {hint && <div className="rpa-field-hint">{hint}</div>}
      {children}
    </div>
  );
}
function CB({ checked, onChange, label }) {
  return (
    <button type="button" className={`tds-cb-row${checked ? ' checked' : ''}`} onClick={() => onChange(!checked)} aria-pressed={checked}>
      <span className="tds-cb-box">{checked ? '✓' : ''}</span>
      <span className="tds-cb-label">{label}</span>
    </button>
  );
}
function RadioCircle({ selected, onSelect, label }) {
  return (
    <button type="button" className={`tds-radio-row${selected ? ' checked' : ''}`} onClick={onSelect} aria-pressed={selected}>
      <span className="tds-radio-circle">{selected && <span className="tds-radio-dot" />}</span>
      <span className="tds-cb-label">{label}</span>
    </button>
  );
}
// Yes/No (or custom option) radio bound to a field whose value is one of enum|null.
function YesNo({ value, onChange, options }) {
  const opts = options || [['Yes', 'Yes'], ['No', 'No']];
  return (
    <div className="tds-yn-group" style={{ minWidth: 120 }}>
      {opts.map(([val, lbl]) => (
        <button
          key={val}
          type="button"
          className={`tds-yn-btn ${val === 'No' || val === 'NoItems' ? 'no' : 'yes'}${value === val ? ' selected' : ''}`}
          onClick={() => onChange(value === val ? null : val)}
        >{lbl}</button>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function TDSWizardModal({ isOpen, onClose, onComplete, onReviewComplete, onProgressUpdate, readOnly, transaction, draftData }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [draftId, setDraftId] = useState(draftData?.id || draftData?.wizardId || null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // All PDF-field values live here, keyed by the exact AcroForm field name.
  const [fields, setFields] = useState({});
  // Explanation textareas (logical keys from EXPLANATION_GROUPS), split into
  // the PDF line fields at payload time.
  const [texts, setTexts] = useState({});

  // Field accessors/mutators (checkbox -> bool, radio -> enum|null, text -> str)
  const cb = (k) => fields[k] === true;
  const setCb = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const rad = (k) => (fields[k] ?? null);
  const setRad = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const txt = (k) => (fields[k] ?? '');
  const setTxt = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const exp = (k) => (texts[k] ?? '');
  const setExp = (k, v) => setTexts((p) => ({ ...p, [k]: v }));

  const getFirebaseToken = () => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
    } else {
      const { onAuthStateChanged } = require('firebase/auth');
      const unsub = onAuthStateChanged(auth, (user) => {
        unsub();
        if (user) user.getIdToken(true).then(resolve).catch(reject);
        else reject(new Error('Not authenticated'));
      });
      setTimeout(() => reject(new Error('Auth timeout')), 10000);
    }
  });

  const getTransactionAddress = () => transaction?.address || transaction?.propertyAddress || '';
  const getTransactionCity = () => transaction?.city || '';
  const getTransactionCounty = () => transaction?.county || transaction?.propertyCounty || '';

  // Which PDF fields belong to which step — drives per-step save payloads.
  const STEP_FIELD_KEYS = {
    1: [
      'property_address_page_1', 'property_city', 'property_county', 'disclosure_as_of_date',
      'property_has_additional_dwelling_units', 'disclosure_applies_only_to_specified_units', 'disclosed_unit_numbers_page_1',
      'seller_occupies_property',
      'substituted_disclosure_contract_inspection_reports', 'substituted_disclosure_additional_reports', 'no_substituted_disclosures',
    ],
    2: CB_KITCHEN.map(([k]) => k),
    3: [
      ...CB_HVAC.map(([k]) => k),
      'property_has_water_heater', ...CB_WATER_HEATER_FUEL.map(([k]) => k),
      ...CB_POOL_SPA.map(([k]) => k), 'property_has_pool_spa_heater', ...CB_POOL_HEATER_FUEL.map(([k]) => k),
      'roof_type', 'roof_approximate_age', 'fireplace_locations', 'gas_starter_details',
      'exhaust_fan_locations', 'wiring_220_volt_locations', 'other_property_items',
    ],
    4: [
      'property_has_water_supply', ...CB_WATER_SOURCE.map(([k]) => k), 'water_supply_other_description',
      'property_has_gas_supply', ...CB_GAS_SOURCE.map(([k]) => k),
      ...CB_SEWER.map(([k]) => k), ...CB_SAFETY.map(([k]) => k), ...CB_MISC.map(([k]) => k),
      'property_has_garage', 'garage_attachment_type', ...CB_GARAGE.map(([k]) => k), 'garage_remote_control_count',
      'items_not_in_operating_condition',
    ],
    5: ['seller_aware_of_significant_defects', ...DEFECTS.map(([k]) => k)],
    6: AWARENESS.map(([k]) => k),
    7: [
      'seller_agent_inspection_disclosure_choice', 'seller_brokerage_inspection',
      'buyer_agent_inspection_disclosure_choice', 'buyer_brokerage_inspection',
    ],
    8: [],
  };

  // Explanation groups to include per step.
  const STEP_EXPLANATIONS = {
    1: ['additional_inspection_reports_description'],
    4: ['nonoperating_items_description'],
    5: ['other_structural_components_description', 'significant_defects_explanation'],
    6: ['seller_awareness_explanation'],
    7: ['seller_agent_inspection_items', 'buyer_agent_inspection_items'],
  };

  const getStepPayload = (stepNumber) => {
    if (stepNumber === TOTAL_STEPS) return { confirmed, submittedAt: new Date().toISOString() };
    const out = {};
    (STEP_FIELD_KEYS[stepNumber] || []).forEach((k) => {
      if (fields[k] !== undefined) out[k] = fields[k];
    });
    (STEP_EXPLANATIONS[stepNumber] || []).forEach((logicalKey) => {
      Object.assign(out, splitIntoLines(exp(logicalKey), EXPLANATION_GROUPS[logicalKey]));
    });
    return out;
  };

  const getCompletedStepsFromDraft = (draft) => {
    const completed = new Set();
    const steps = draft?.steps || {};
    Object.entries(steps).forEach(([num, data]) => {
      if (data?.stepStatus === 'COMPLETED' || data?.completed === true) completed.add(Number(num));
    });
    if (completed.size === 0 && Array.isArray(draft?.completedSteps)) draft.completedSteps.forEach((n) => completed.add(Number(n)));
    return Array.from(completed).filter((n) => n >= 1 && n <= TOTAL_STEPS).sort((a, b) => a - b);
  };

  // ── Body scroll lock ─────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ── Pre-fill / restore ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    if (draftData) {
      const restoredStep = draftData.currentStep || 1;
      setCurrentStep(restoredStep);
      setDraftId(draftData.id || draftData.wizardId || null);
      const completedNums = getCompletedStepsFromDraft(draftData);
      setCompletedSteps(new Set(completedNums));
      if (onProgressUpdate) onProgressUpdate(completedNums, restoredStep);

      // Each step's data is already schema-keyed — merge them all into `fields`.
      const mergedFields = {};
      const mergedTexts = {};
      Object.values(draftData.steps || {}).forEach((stepData) => {
        const data = stepData?.data || {};
        Object.entries(data).forEach(([k, v]) => {
          if (k === 'confirmed' || k === 'submittedAt') return;
          mergedFields[k] = v;
        });
      });
      // Rebuild explanation textareas by joining their line fields back.
      Object.entries(EXPLANATION_GROUPS).forEach(([logicalKey, lineKeys]) => {
        const joined = joinLines(mergedFields, lineKeys);
        if (joined) mergedTexts[logicalKey] = joined;
      });

      // Prefill identity defaults from the transaction when the draft lacks them.
      if (!mergedFields.property_address_page_1) mergedFields.property_address_page_1 = getTransactionAddress();
      if (!mergedFields.property_city) mergedFields.property_city = getTransactionCity();
      if (!mergedFields.property_county) mergedFields.property_county = getTransactionCounty();

      setFields(mergedFields);
      setTexts(mergedTexts);
      return;
    }

    // No draft — seed identity fields from the transaction.
    setCurrentStep(1);
    setFields({
      property_address_page_1: getTransactionAddress(),
      property_city: getTransactionCity(),
      property_county: getTransactionCounty(),
    });
    setTexts({});
  }, [isOpen, draftData, transaction]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  // ── Navigation ───────────────────────────────────────────────────────────────
  const progressPercent = Math.round(((currentStep - 1) / (TOTAL_STEPS - 1)) * 100);
  const propertyLabel = txt('property_address_page_1')
    ? `${txt('property_address_page_1')}${txt('property_city') ? `, ${txt('property_city')}` : ''}`
    : (transaction && (transaction.address || transaction.propertyAddress)) || 'New TDS';

  const goToStep = async (step, isForward) => {
    if (!readOnly && draftId && transaction?.id) {
      if (isForward) {
        const newCompleted = new Set([...completedSteps, currentStep]);
        setCompletedSteps(newCompleted);
        setIsSaving(true);
        setSaveError(null);
        try {
          const token = await getFirebaseToken();
          await apiClient.put(
            API_ENDPOINTS.tdsWizardSaveStep(transaction.id, draftId, currentStep, true),
            { data: getStepPayload(currentStep) },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (onProgressUpdate) onProgressUpdate([...newCompleted], step);
        } catch (err) {
          console.warn('[TDS] Step save failed:', err?.message);
          setSaveError('Failed to save step. Your progress may not be saved.');
          setIsSaving(false);
          return;
        } finally {
          setIsSaving(false);
        }
      } else {
        apiClient.put(
          API_ENDPOINTS.tdsWizardSaveStep(transaction.id, draftId, currentStep, false),
          { data: getStepPayload(currentStep) },
          { headers: { Authorization: `Bearer ${await getFirebaseToken()}` } },
        ).catch((err) => console.warn('[TDS] Back/jump save failed:', err?.message));
      }
    }
    setCurrentStep(step);
  };

  const handleNext = async () => { await goToStep(Math.min(currentStep + 1, TOTAL_STEPS), true); };
  const handleBack = () => { if (currentStep > 1) goToStep(currentStep - 1, false); };
  const handleStepClick = (stepId) => { goToStep(stepId, stepId > currentStep); };

  const handleGenerateTDS = async () => {
    if (!confirmed) return;
    if (draftId && transaction?.id) {
      setIsSaving(true);
      setSaveError(null);
      try {
        const token = await getFirebaseToken();
        for (let step = 1; step < TOTAL_STEPS; step += 1) {
          if (!completedSteps.has(step)) {
            await apiClient.put(
              API_ENDPOINTS.tdsWizardSaveStep(transaction.id, draftId, step, true),
              { data: getStepPayload(step) },
              { headers: { Authorization: `Bearer ${token}` } },
            );
          }
        }
        await apiClient.put(
          API_ENDPOINTS.tdsWizardSaveStep(transaction.id, draftId, TOTAL_STEPS, true),
          { data: getStepPayload(TOTAL_STEPS) },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await apiClient.post(
          API_ENDPOINTS.tdsWizardSubmit(transaction.id, draftId),
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const allCompleted = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);
        setCompletedSteps(new Set(allCompleted));
        if (onProgressUpdate) onProgressUpdate(allCompleted, TOTAL_STEPS);
      } catch (err) {
        console.warn('[TDS] Submit failed:', err?.message);
        setSaveError('Failed to submit TDS. Please try again.');
        setIsSaving(false);
        return;
      }
      setIsSaving(false);
    }
    if (onComplete) onComplete();
    else onClose();
  };

  const handleCompleteReview = async () => {
    if (!readOnly || !transaction?.id) { onClose(); return; }
    const propertyId = draftData?.interestedPropertyId || transaction?.propertyId;
    if (!propertyId) { setSaveError('Missing property reference for review completion.'); return; }
    setIsSaving(true);
    setSaveError(null);
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.tdsWizardLock(transaction.id, propertyId),
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (onReviewComplete) await onReviewComplete();
      else onClose();
    } catch (err) {
      console.warn('[TDS] Review completion failed:', err?.message);
      setSaveError('Failed to complete TDS review. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  // ── Reusable renderers ───────────────────────────────────────────────────────
  const CheckGrid = ({ items }) => (
    <div className="tds-appliance-grid">
      {items.map(([k, label]) => (
        <button key={k} type="button" className={`tds-appliance-item${cb(k) ? ' checked' : ''}`} onClick={() => setCb(k, !cb(k))}>
          <span className="tds-appliance-box">{cb(k) ? '✓' : ''}</span>{label}
        </button>
      ))}
    </div>
  );

  const QRadio = ({ fieldKey, label, options }) => (
    <div className={`tds-defect-row${rad(fieldKey) === 'Yes' ? ' flagged' : ''}`}>
      <span className="tds-defect-label">{label}</span>
      <YesNo value={rad(fieldKey)} onChange={(v) => setRad(fieldKey, v)} options={options} />
    </div>
  );

  const ExpText = ({ logicalKey, label, hint, placeholder }) => (
    <Field label={label} hint={hint}>
      <textarea className="rpa-input" style={{ minHeight: 70, resize: 'vertical' }} placeholder={placeholder}
        value={exp(logicalKey)} onChange={(e) => setExp(logicalKey, e.target.value)} />
    </Field>
  );

  // ── Steps ────────────────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div>
      <div className="rpa-card">
        <InfoBox type="info"><strong>TDS — Real Estate Transfer Disclosure Statement</strong> (C.A.R. Form TDS, Rev. 6/24). Seller discloses the condition of the property per Civil Code §1102.</InfoBox>
        <SectionTitle>Property</SectionTitle>
        <Field label="Property Address" required>
          <input className="rpa-input" value={txt('property_address_page_1')} onChange={(e) => setTxt('property_address_page_1', e.target.value)} placeholder="e.g. 1902 Coldwater Canyon Drive" />
        </Field>
        <div className="rpa-field-row">
          <Field label="City"><input className="rpa-input" value={txt('property_city')} onChange={(e) => setTxt('property_city', e.target.value)} /></Field>
          <Field label="County"><input className="rpa-input" value={txt('property_county')} onChange={(e) => setTxt('property_county', e.target.value)} /></Field>
          <Field label="Date of Disclosure"><input className="rpa-input" type="date" value={txt('disclosure_as_of_date')} onChange={(e) => setTxt('disclosure_as_of_date', e.target.value)} /></Field>
        </div>
        <CB checked={cb('property_has_additional_dwelling_units')} onChange={(v) => setCb('property_has_additional_dwelling_units', v)} label="Property is a duplex, triplex, fourplex, or has additional dwelling units" />
        {cb('property_has_additional_dwelling_units') && (
          <div className="rpa-toggle-content">
            <CB checked={cb('disclosure_applies_only_to_specified_units')} onChange={(v) => setCb('disclosure_applies_only_to_specified_units', v)} label="This TDS is for only specific unit(s)" />
            {cb('disclosure_applies_only_to_specified_units') && (
              <Field label="Unit(s)"><input className="rpa-input" value={txt('disclosed_unit_numbers_page_1')} onChange={(e) => setTxt('disclosed_unit_numbers_page_1', e.target.value)} placeholder="e.g. Unit A, Unit B" /></Field>
            )}
          </div>
        )}
      </div>

      <div className="rpa-card">
        <SectionTitle>Seller Occupancy</SectionTitle>
        <QRadio fieldKey="seller_occupies_property" label="Seller is occupying the property?" />
      </div>

      <div className="rpa-card">
        <SectionTitle>§I — Coordination with Other Disclosure Forms (Substituted Disclosures)</SectionTitle>
        <CB checked={cb('substituted_disclosure_contract_inspection_reports')} onChange={(v) => setCb('substituted_disclosure_contract_inspection_reports', v)} label="Inspection reports completed pursuant to the contract of sale or receipt for deposit" />
        <CB checked={cb('substituted_disclosure_additional_reports')} onChange={(v) => setCb('substituted_disclosure_additional_reports', v)} label="Additional inspection reports or disclosures" />
        {cb('substituted_disclosure_additional_reports') && (
          <ExpText logicalKey="additional_inspection_reports_description" label="Describe additional reports/disclosures" placeholder="List additional inspection reports or disclosures…" />
        )}
        <CB checked={cb('no_substituted_disclosures')} onChange={(v) => setCb('no_substituted_disclosures', v)} label="No substituted disclosures for this transfer" />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="rpa-card">
      <InfoBox type="info"><strong>§II.A — The subject property has the items checked below.</strong></InfoBox>
      <SectionTitle>Kitchen & Laundry</SectionTitle>
      <CheckGrid items={CB_KITCHEN} />
    </div>
  );

  const renderStep3 = () => (
    <div>
      <div className="rpa-card">
        <SectionTitle>Heating & Cooling</SectionTitle>
        <CheckGrid items={CB_HVAC} />
      </div>
      <div className="rpa-card">
        <SectionTitle>Water Heater</SectionTitle>
        <CB checked={cb('property_has_water_heater')} onChange={(v) => setCb('property_has_water_heater', v)} label="Water Heater" />
        {cb('property_has_water_heater') && <div className="rpa-toggle-content"><Field label="Energy source"><CheckGrid items={CB_WATER_HEATER_FUEL} /></Field></div>}
      </div>
      <div className="rpa-card">
        <SectionTitle>Pool / Spa</SectionTitle>
        <CheckGrid items={CB_POOL_SPA} />
        <CB checked={cb('property_has_pool_spa_heater')} onChange={(v) => setCb('property_has_pool_spa_heater', v)} label="Pool/Spa Heater" />
        {cb('property_has_pool_spa_heater') && <div className="rpa-toggle-content"><Field label="Energy source"><CheckGrid items={CB_POOL_HEATER_FUEL} /></Field></div>}
      </div>
      <div className="rpa-card">
        <SectionTitle>Roof & Other</SectionTitle>
        <div className="rpa-field-row">
          <Field label="Roof(s) Type"><input className="rpa-input" value={txt('roof_type')} onChange={(e) => setTxt('roof_type', e.target.value)} /></Field>
          <Field label="Roof Age (approx.)"><input className="rpa-input" value={txt('roof_approximate_age')} onChange={(e) => setTxt('roof_approximate_age', e.target.value)} /></Field>
        </div>
        <Field label="Fireplace(s) in"><input className="rpa-input" value={txt('fireplace_locations')} onChange={(e) => setTxt('fireplace_locations', e.target.value)} placeholder="Location(s)" /></Field>
        <Field label="Gas Starter"><input className="rpa-input" value={txt('gas_starter_details')} onChange={(e) => setTxt('gas_starter_details', e.target.value)} placeholder="Details" /></Field>
        <div className="rpa-field-row">
          <Field label="Exhaust Fan(s) in"><input className="rpa-input" value={txt('exhaust_fan_locations')} onChange={(e) => setTxt('exhaust_fan_locations', e.target.value)} /></Field>
          <Field label="220 Volt Wiring in"><input className="rpa-input" value={txt('wiring_220_volt_locations')} onChange={(e) => setTxt('wiring_220_volt_locations', e.target.value)} /></Field>
        </div>
        <Field label="Other"><input className="rpa-input" value={txt('other_property_items')} onChange={(e) => setTxt('other_property_items', e.target.value)} /></Field>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div>
      <div className="rpa-card">
        <SectionTitle>Water Supply</SectionTitle>
        <CB checked={cb('property_has_water_supply')} onChange={(v) => setCb('property_has_water_supply', v)} label="Water Supply" />
        {cb('property_has_water_supply') && (
          <div className="rpa-toggle-content">
            <Field label="Source"><CheckGrid items={CB_WATER_SOURCE} /></Field>
            {cb('water_supply_source_private_utility_or') && <Field label="Other (describe)"><input className="rpa-input" value={txt('water_supply_other_description')} onChange={(e) => setTxt('water_supply_other_description', e.target.value)} /></Field>}
          </div>
        )}
      </div>
      <div className="rpa-card">
        <SectionTitle>Gas Supply</SectionTitle>
        <CB checked={cb('property_has_gas_supply')} onChange={(v) => setCb('property_has_gas_supply', v)} label="Gas Supply" />
        {cb('property_has_gas_supply') && <div className="rpa-toggle-content"><Field label="Source"><CheckGrid items={CB_GAS_SOURCE} /></Field></div>}
      </div>
      <div className="rpa-card">
        <SectionTitle>Sewer & Drainage</SectionTitle>
        <CheckGrid items={CB_SEWER} />
      </div>
      <div className="rpa-card">
        <SectionTitle>Safety & Security</SectionTitle>
        <CheckGrid items={CB_SAFETY} />
      </div>
      <div className="rpa-card">
        <SectionTitle>Exterior & Misc</SectionTitle>
        <CheckGrid items={CB_MISC} />
      </div>
      <div className="rpa-card">
        <SectionTitle>Garage</SectionTitle>
        <CB checked={cb('property_has_garage')} onChange={(v) => setCb('property_has_garage', v)} label="Garage" />
        {cb('property_has_garage') && (
          <div className="rpa-toggle-content" style={{ display: 'flex', gap: 16 }}>
            <RadioCircle selected={rad('garage_attachment_type') === 'Attached'} onSelect={() => setRad('garage_attachment_type', rad('garage_attachment_type') === 'Attached' ? null : 'Attached')} label="Attached" />
            <RadioCircle selected={rad('garage_attachment_type') === 'NotAttached'} onSelect={() => setRad('garage_attachment_type', rad('garage_attachment_type') === 'NotAttached' ? null : 'NotAttached')} label="Not Attached" />
          </div>
        )}
        <CheckGrid items={CB_GARAGE} />
        {cb('property_has_garage_remote_controls') && <Field label="Number of Remote Controls"><input className="rpa-input" value={txt('garage_remote_control_count')} onChange={(e) => setTxt('garage_remote_control_count', e.target.value)} placeholder="e.g. 2" /></Field>}
      </div>
      <div className="rpa-card">
        <SectionTitle>Operating Condition</SectionTitle>
        <QRadio fieldKey="items_not_in_operating_condition" label="Are any of the above items NOT in operating condition?" />
        {rad('items_not_in_operating_condition') === 'Yes' && (
          <ExpText logicalKey="nonoperating_items_description" label="If yes, describe" placeholder="Describe items not in operating condition…" />
        )}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="rpa-card">
      <InfoBox type="warn"><strong>§II.B — Significant Defects / Malfunctions.</strong> Are you (Seller) aware of any significant defects/malfunctions in any of the following?</InfoBox>
      <QRadio fieldKey="seller_aware_of_significant_defects" label="Aware of any significant defects/malfunctions?" />
      {rad('seller_aware_of_significant_defects') === 'Yes' && (
        <>
          <SectionTitle>Check all that apply</SectionTitle>
          <CheckGrid items={DEFECTS} />
          {cb('defect_in_other_structural_components') && (
            <ExpText logicalKey="other_structural_components_description" label="Describe other structural components" placeholder="Describe…" />
          )}
          <ExpText logicalKey="significant_defects_explanation" label="Explanation (if any of the above is checked)" placeholder="Explain the checked defects/malfunctions…" />
        </>
      )}
    </div>
  );

  const renderStep6 = () => (
    <div className="rpa-card">
      <InfoBox type="warn"><strong>§II.C — Are you (Seller) aware of any of the following?</strong></InfoBox>
      {AWARENESS.map(([k, label]) => <QRadio key={k} fieldKey={k} label={label} />)}
      <ExpText logicalKey="seller_awareness_explanation" label="Explanation for all §II.C 'Yes' answers" placeholder="Reference the question number and explain…" />
    </div>
  );

  const renderStep7 = () => (
    <div>
      <div className="rpa-card">
        <InfoBox type="info"><strong>§III — Agent's Inspection Disclosure (Listing Agent).</strong> To be completed only if the Seller is represented by an agent.</InfoBox>
        <Field label="Brokerage (Representing Seller)"><input className="rpa-input" value={txt('seller_brokerage_inspection')} onChange={(e) => setTxt('seller_brokerage_inspection', e.target.value)} /></Field>
        <RadioCircle selected={rad('seller_agent_inspection_disclosure_choice') === 'AttachedAVID'} onSelect={() => setRad('seller_agent_inspection_disclosure_choice', 'AttachedAVID')} label="See attached Agent Visual Inspection Disclosure (AVID)" />
        <RadioCircle selected={rad('seller_agent_inspection_disclosure_choice') === 'NoItems'} onSelect={() => setRad('seller_agent_inspection_disclosure_choice', 'NoItems')} label="Agent notes no items for disclosure" />
        <RadioCircle selected={rad('seller_agent_inspection_disclosure_choice') === 'ItemsBelow'} onSelect={() => setRad('seller_agent_inspection_disclosure_choice', 'ItemsBelow')} label="Agent notes the following items" />
        {rad('seller_agent_inspection_disclosure_choice') === 'ItemsBelow' && (
          <ExpText logicalKey="seller_agent_inspection_items" label="Items noted" placeholder="List items…" />
        )}
      </div>
      <div className="rpa-card">
        <InfoBox type="info"><strong>§IV — Agent's Inspection Disclosure (Buyer's Agent).</strong> To be completed only if the agent who obtained the offer is other than the agent above.</InfoBox>
        <Field label="Brokerage (Obtaining the Offer)"><input className="rpa-input" value={txt('buyer_brokerage_inspection')} onChange={(e) => setTxt('buyer_brokerage_inspection', e.target.value)} /></Field>
        <RadioCircle selected={rad('buyer_agent_inspection_disclosure_choice') === 'AttachedAVID'} onSelect={() => setRad('buyer_agent_inspection_disclosure_choice', 'AttachedAVID')} label="See attached Agent Visual Inspection Disclosure (AVID)" />
        <RadioCircle selected={rad('buyer_agent_inspection_disclosure_choice') === 'NoItems'} onSelect={() => setRad('buyer_agent_inspection_disclosure_choice', 'NoItems')} label="Agent notes no items for disclosure" />
        <RadioCircle selected={rad('buyer_agent_inspection_disclosure_choice') === 'ItemsBelow'} onSelect={() => setRad('buyer_agent_inspection_disclosure_choice', 'ItemsBelow')} label="Agent notes the following items" />
        {rad('buyer_agent_inspection_disclosure_choice') === 'ItemsBelow' && (
          <ExpText logicalKey="buyer_agent_inspection_items" label="Items noted" placeholder="List items…" />
        )}
      </div>
    </div>
  );

  const renderStep8 = () => (
    <div className="rpa-card">
      <InfoBox type="info"><strong>Review &amp; Confirm.</strong> Confirm the TDS disclosures below. Signatures are collected via DocuSign after generation.</InfoBox>
      <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>
        <div><strong>Property:</strong> {txt('property_address_page_1') || '—'}{txt('property_city') ? `, ${txt('property_city')}` : ''}</div>
        <div><strong>Date of Disclosure:</strong> {txt('disclosure_as_of_date') || '—'}</div>
        <div><strong>Seller occupying:</strong> {rad('seller_occupies_property') || '—'}</div>
        <div><strong>Significant defects aware:</strong> {rad('seller_aware_of_significant_defects') || '—'}</div>
        <div><strong>§II.C questions answered:</strong> {AWARENESS.filter(([k]) => rad(k) != null).length} / {AWARENESS.length}</div>
      </div>
      <label className="tds-confirm-row" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 3 }} />
        <span style={{ fontSize: 13 }}>I confirm the above disclosures are true and complete to the best of the Seller's knowledge, and authorize generation of the TDS for signature.</span>
      </label>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      default: return renderStep1();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="rpa-overlay" role="dialog" aria-modal="true" aria-label="TDS Wizard" onClick={handleOverlayClick}>
      <div className="rpa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rpa-header">
          <div className="rpa-header-left">
            <span className="rpa-header-title">TDS Wizard</span>
            {readOnly && <span style={{ fontSize: 11, fontWeight: 600, color: '#7C3AED', background: '#EDE9FE', borderRadius: 4, padding: '2px 7px', marginLeft: 8, letterSpacing: '0.02em' }}>READ ONLY</span>}
            <span className="rpa-header-sub">{propertyLabel}</span>
          </div>
          <button className="rpa-close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="rpa-progress-track"><div className="rpa-progress-bar" style={{ width: `${progressPercent}%` }} /></div>

        <div className="rpa-body">
          <nav className="rpa-sidebar" aria-label="Wizard steps">
            {STEPS.map((step) => {
              const isDone = completedSteps.has(step.id);
              const isActive = step.id === currentStep;
              return (
                <button key={step.id} type="button" className={`rpa-step-item${isActive ? ' active' : ''}${isDone ? ' done' : ''}`} onClick={() => handleStepClick(step.id)} aria-current={isActive ? 'step' : undefined}>
                  <span className="rpa-step-icon">{STEP_ICONS[step.id]}</span>
                  <span className="rpa-step-meta">
                    <span className="rpa-step-num">Step {step.id}</span>
                    <span className="rpa-step-title">{step.title}</span>
                  </span>
                  {isDone && <span className="rpa-step-check">✓</span>}
                </button>
              );
            })}
          </nav>

          <main className="rpa-content">
            <div style={readOnly ? { pointerEvents: 'none', userSelect: 'none' } : undefined}>{renderCurrentStep()}</div>
          </main>
        </div>

        <div className="rpa-footer">
          <div className="rpa-footer-left">
            {currentStep > 1 && <button className="rpa-btn rpa-btn-ghost" onClick={handleBack}>← Back</button>}
          </div>
          <div className="rpa-footer-center"><span className="rpa-footer-progress">Step {currentStep} of {TOTAL_STEPS}</span></div>
          <div className="rpa-footer-right">
            {saveError && <span style={{ fontSize: 12, color: '#C0392B', marginRight: 12 }}>{saveError}</span>}
            {currentStep < TOTAL_STEPS && (
              <button className="rpa-btn rpa-btn-primary" onClick={handleNext} disabled={!readOnly && isSaving}>
                {!readOnly && isSaving ? 'Saving…' : readOnly ? 'Next →' : 'Save & Continue →'}
              </button>
            )}
            {currentStep === TOTAL_STEPS && (
              readOnly ? (
                <button className="rpa-btn rpa-btn-primary" onClick={handleCompleteReview} disabled={isSaving}>{isSaving ? 'Completing…' : 'Complete Review'}</button>
              ) : (
                <button className={`rpa-btn${confirmed && !isSaving ? ' rpa-btn-primary' : ' rpa-btn-disabled'}`} onClick={handleGenerateTDS} disabled={!confirmed || isSaving}>{isSaving ? 'Submitting…' : 'Generate TDS'}</button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TDSWizardModal;
